import { keyframes } from '@emotion/react';
import { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { Box, Portal, Text } from '@bonusly/design-system';

import { ReactComponent as ConnectorLong } from './assets/connector-long.svg';
import { ReactComponent as ConnectorRight } from './assets/connector-right.svg';
import { ReactComponent as ConnectorShort } from './assets/connector-short.svg';
import { ReactComponent as ConnectorWide } from './assets/connector-wide.svg';
import { ConnectorDirection, ConnectorVariant, OnboardingArrowProps } from './types';

// Fade in animation
const fadeIn = keyframes`
	from {
		opacity: 0;
	}
	to {
		opacity: 1;
	}
`;

// Connector dimensions by variant
const CONNECTOR_DIMENSIONS: Record<ConnectorVariant, { width: number; height: number }> = {
	tiny: { width: 58, height: 17 },
	short: { width: 58, height: 47 },
	long: { width: 92, height: 48 },
	wide: { width: 79, height: 88 },
};

// Dimensions for the straight horizontal connector used by center-right direction
const CENTER_RIGHT_CONNECTOR = { width: 80, height: 30 };

// Gap between text and connector/target edges
const TEXT_GAP = 16;

/**
 * Get the connector component for a given variant.
 */
const getConnectorComponent = (variant: ConnectorVariant) => {
	switch (variant) {
		case 'tiny':
			return ConnectorShort;
		case 'short':
			return ConnectorShort;
		case 'long':
			return ConnectorLong;
		case 'wide':
			return ConnectorWide;
		default:
			return ConnectorShort;
	}
};

/**
 * Get CSS transform to rotate/mirror the base SVG for different directions.
 *
 * Each variant has different base orientations:
 * - Short/Long: dot at bottom-left, arrow at top-right pointing up
 * - Wide: dot at bottom-left, arrow at right edge pointing right
 */
const getConnectorTransform = (direction: ConnectorDirection, variant: ConnectorVariant): string => {
	// Center-right: straight horizontal arrow, no transform needed
	if (direction === 'center-right') {
		return 'none';
	}

	// Wide variant: connector comes from left side, only flip vertically for top positions
	if (variant === 'wide') {
		switch (direction) {
			case 'bottom-left':
			case 'bottom-right':
				// Arrow points right to target, dot at bottom-left - no transform
				return 'none';
			case 'top-left':
			case 'top-right':
				// Flip vertically so arrow still points right, but dot is at top-left
				return 'scaleY(-1)';
			default:
				return 'none';
		}
	}

	// Short/Long variants: base has dot at bottom-left, arrow at top-right pointing up
	switch (direction) {
		case 'bottom-left':
			// Base position - no transform needed
			return 'none';
		case 'bottom-right':
			// Mirror horizontally - dot moves to bottom-right
			return 'scaleX(-1)';
		case 'top-left':
			// Mirror vertically - arrow points down, dot at top-left
			return 'scaleY(-1)';
		case 'top-right':
			// Mirror both - arrow points down, dot at top-right
			return 'scale(-1, -1)';
		default:
			return 'none';
	}
};

/**
 * Calculate the position of the connector and text relative to the target element.
 *
 * Each variant has different base orientations:
 * - Short/Long: dot at bottom-left, arrow at top-right pointing up
 * - Wide: dot at bottom-left, arrow at right edge pointing right
 */
const calculatePosition = (
	targetRect: DOMRect,
	direction: ConnectorDirection,
	variant: ConnectorVariant,
	offset: number
): { connectorTop: number; connectorLeft: number; textTop: number; textLeft: number; textAlign: 'left' | 'right' } => {
	const { width: connectorWidth, height: connectorHeight } = CONNECTOR_DIMENSIONS[variant];

	let connectorTop = 0;
	let connectorLeft = 0;
	let textTop = 0;
	let textLeft = 0;
	let textAlign: 'left' | 'right' = 'left';

	// Center-right: straight horizontal arrow to the right of target, pointing left
	if (direction === 'center-right') {
		const { height: crHeight } = CENTER_RIGHT_CONNECTOR;
		// Arrow tip (left edge) points at the right edge of the target, vertically centered
		connectorTop = targetRect.top + targetRect.height / 2 - crHeight / 2;
		connectorLeft = targetRect.right + offset;
		// Text right edge is textGap from the target's right edge, vertically centered.
		// Offset shifts the entire unit (connector + text) left from the default position.
		textTop = connectorTop + crHeight / 2;
		textLeft = targetRect.right - TEXT_GAP - offset; // desired RIGHT edge of the text
		textAlign = 'right';
		return { connectorTop, connectorLeft, textTop, textLeft, textAlign };
	}

	// Wide variant: connector comes from the LEFT side of the target, arrow points RIGHT
	if (variant === 'wide') {
		const arrowOffsetY = 15; // Arrow is about 15px from top of SVG

		switch (direction) {
			case 'bottom-left':
			case 'bottom-right':
				// Connector to the left of target, arrow points right to target
				connectorTop = targetRect.top + targetRect.height / 2 - arrowOffsetY;
				connectorLeft = targetRect.left - connectorWidth - offset;
				// Text goes below connector, at the dot (bottom-left)
				textTop = connectorTop + connectorHeight + TEXT_GAP;
				textLeft = connectorLeft + 4;
				textAlign = 'left';
				break;

			case 'top-left':
			case 'top-right':
				// Flipped vertically, arrow still points right
				connectorTop = targetRect.top + targetRect.height / 2 - (connectorHeight - arrowOffsetY);
				connectorLeft = targetRect.left - connectorWidth - offset;
				// Text goes above connector, at the dot (now at top-left)
				textTop = connectorTop - TEXT_GAP;
				textLeft = connectorLeft + 4;
				textAlign = 'left';
				break;
		}

		return { connectorTop, connectorLeft, textTop, textLeft, textAlign };
	}

	// Short/Long variants: base has dot at bottom-left, arrow at top-right pointing up
	const arrowOffsetX = connectorWidth - 15;

	switch (direction) {
		case 'bottom-left':
			// Base position: arrow points UP to target, dot at bottom-left
			connectorTop = targetRect.bottom + offset;
			connectorLeft = targetRect.left + targetRect.width / 2 - arrowOffsetX;
			// Text below connector, at dot (bottom-left)
			textTop = connectorTop + connectorHeight + TEXT_GAP;
			textLeft = connectorLeft + 4;
			textAlign = 'left';
			break;

		case 'bottom-right':
			// Flipped horizontally: arrow points UP, dot at bottom-right
			connectorTop = targetRect.bottom + offset;
			connectorLeft = targetRect.left + targetRect.width / 2 - (connectorWidth - arrowOffsetX);
			// Text goes below connector, aligned with dot (right side)
			textTop = connectorTop + connectorHeight + TEXT_GAP;
			textLeft = connectorLeft + connectorWidth - 4;
			textAlign = 'right';
			break;

		case 'top-left':
			// Arrow points DOWN to target (mirrored vertically), dot is at TOP-LEFT
			connectorTop = targetRect.top - connectorHeight - offset;
			connectorLeft = targetRect.left + targetRect.width / 2 - arrowOffsetX;
			// Text goes above connector, aligned with dot (left side)
			textTop = connectorTop - TEXT_GAP;
			textLeft = connectorLeft + 4;
			textAlign = 'left';
			break;

		case 'top-right':
			// Arrow points DOWN to target (mirrored both), dot is at TOP-RIGHT
			connectorTop = targetRect.top - connectorHeight - offset;
			connectorLeft = targetRect.left + targetRect.width / 2 - (connectorWidth - arrowOffsetX);
			// Text goes above connector, aligned with dot (right side)
			textTop = connectorTop - TEXT_GAP;
			textLeft = connectorLeft + connectorWidth - 4;
			textAlign = 'right';
			break;
	}

	return { connectorTop, connectorLeft, textTop, textLeft, textAlign };
};

/**
 * Custom hook to wait for an element to appear in the DOM.
 * Uses MutationObserver with polling fallback for reliability.
 */
const useWaitForElement = (selector: string, timeoutMs: number, isVisible: boolean) => {
	const [element, setElement] = useState<Element | null>(null);

	useEffect(() => {
		if (!isVisible) {
			setElement(null);
			return;
		}

		// Try to find the element immediately
		const foundElement = document.querySelector(selector);
		if (foundElement) {
			setElement(foundElement);
			return;
		}

		// Set up MutationObserver to watch for the element
		let observer: MutationObserver | null = null;
		let pollInterval: NodeJS.Timeout | null = null;
		let timeoutId: NodeJS.Timeout | null = null;

		const cleanup = () => {
			if (observer) {
				observer.disconnect();
				observer = null;
			}
			if (pollInterval) {
				clearInterval(pollInterval);
				pollInterval = null;
			}
			if (timeoutId) {
				clearTimeout(timeoutId);
				timeoutId = null;
			}
		};

		const checkForElement = () => {
			const el = document.querySelector(selector);
			if (el) {
				setElement(el);
				cleanup();
				return true;
			}
			return false;
		};

		// MutationObserver callback
		const observerCallback: MutationCallback = () => {
			checkForElement();
		};

		// Start observing DOM changes
		observer = new MutationObserver(observerCallback);
		observer.observe(document.body, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ['id', 'class', 'data-testid'],
		});

		// Polling fallback (some edge cases might not trigger MutationObserver)
		pollInterval = setInterval(() => {
			checkForElement();
		}, 200);

		// Timeout - stop waiting after specified time
		timeoutId = setTimeout(() => {
			cleanup();
		}, timeoutMs);

		return cleanup;
	}, [selector, timeoutMs, isVisible]);

	return element;
};

/**
 * OnboardingArrow component renders a connector line with a text label
 * positioned relative to a target element on the page.
 */
export const OnboardingArrow: FC<OnboardingArrowProps> = ({ step, isVisible, zIndex }) => {
	const textRef = useRef<HTMLDivElement>(null);
	const [textSize, setTextSize] = useState<{ width: number; height: number } | null>(null);
	const [isTargetVisible, setIsTargetVisible] = useState(true);
	const [position, setPosition] = useState<{
		connectorTop: number;
		connectorLeft: number;
		textTop: number;
		textLeft: number;
		textAlign: 'left' | 'right';
	} | null>(null);

	const direction = step.direction ?? 'top-right';
	const variant = step.variant ?? 'short';
	const offset = step.offset ?? 8;
	const waitForSelectorMs = step.waitForSelectorMs ?? 10000;

	// Wait for the target element to appear in the DOM
	const targetElement = useWaitForElement(step.selector, waitForSelectorMs, isVisible);

	// Measure text dimensions after render
	useLayoutEffect(() => {
		if (!isVisible) {
			setTextSize(null);
			return;
		}
		if (textRef.current) {
			const { offsetWidth, offsetHeight } = textRef.current;
			if (offsetWidth > 0 && offsetHeight > 0) {
				setTextSize((prev) => {
					// Only update if changed to avoid unnecessary re-renders
					if (prev?.width === offsetWidth && prev?.height === offsetHeight) {
						return prev;
					}
					return { width: offsetWidth, height: offsetHeight };
				});
			}
		}
	});

	// Track if target element is visible (not clipped by overflow scroll containers like carousels)
	useEffect(() => {
		if (!targetElement || !isVisible) {
			setIsTargetVisible(true);
			return;
		}

		const observer = new IntersectionObserver(
			([entry]) => {
				setIsTargetVisible(entry.isIntersecting);
			},
			{ threshold: 0.5 }
		);
		observer.observe(targetElement);

		return () => observer.disconnect();
	}, [targetElement, isVisible]);

	// Calculate position when the element is found or when it moves
	useEffect(() => {
		if (!targetElement || !isVisible) {
			setPosition(null);
			return;
		}

		const calculateAndSetPosition = () => {
			const rect = targetElement.getBoundingClientRect();
			// Only update position if element is actually visible (has dimensions)
			if (rect.width > 0 && rect.height > 0) {
				const pos = calculatePosition(rect, direction, variant, offset);
				setPosition(pos);
			}
		};

		// Initial calculation
		calculateAndSetPosition();

		// Recalculate on scroll or resize
		const handleUpdate = () => calculateAndSetPosition();
		window.addEventListener('scroll', handleUpdate, true);
		window.addEventListener('resize', handleUpdate);

		// Also use ResizeObserver to detect element size changes
		const resizeObserver = new ResizeObserver(handleUpdate);
		resizeObserver.observe(targetElement);

		// Use rAF polling to catch layout shifts (e.g. drawer collapse/expand)
		// that don't trigger scroll, resize, or ResizeObserver on the target
		let rafId: number;
		let lastLeft = targetElement.getBoundingClientRect().left;
		let lastTop = targetElement.getBoundingClientRect().top;
		const pollPosition = () => {
			const rect = targetElement.getBoundingClientRect();
			if (rect.left !== lastLeft || rect.top !== lastTop) {
				lastLeft = rect.left;
				lastTop = rect.top;
				calculateAndSetPosition();
			}
			rafId = requestAnimationFrame(pollPosition);
		};
		rafId = requestAnimationFrame(pollPosition);

		return () => {
			window.removeEventListener('scroll', handleUpdate, true);
			window.removeEventListener('resize', handleUpdate);
			resizeObserver.disconnect();
			cancelAnimationFrame(rafId);
		};
	}, [targetElement, direction, variant, offset, isVisible]);

	// Don't render if not visible or target is scrolled out of view
	if (!isVisible || !isTargetVisible) {
		return null;
	}

	const ConnectorComponent = direction === 'center-right' ? ConnectorRight : getConnectorComponent(variant);
	const connectorTransform = getConnectorTransform(direction, variant);

	// Check if we have everything needed to display
	const hasMeasured = textSize !== null && textSize.width > 0 && textSize.height > 0;
	const isReady = targetElement && position && hasMeasured;

	// Calculate final text position based on direction and measured dimensions
	const isRightAlign = position?.textAlign === 'right';
	const isTopPosition = direction === 'top-left' || direction === 'top-right';
	const isCenterRight = direction === 'center-right';

	let finalTextLeft = position?.textLeft ?? 0;
	let finalTextTop = position?.textTop ?? 0;
	let finalConnectorLeft = position?.connectorLeft ?? 0;

	if (isReady && textSize) {
		if (isCenterRight) {
			// textLeft is the desired right edge; subtract width to get left edge
			finalTextLeft = position.textLeft - textSize.width;
			finalTextTop = position.textTop - textSize.height / 2;
			finalConnectorLeft = finalTextLeft - CENTER_RIGHT_CONNECTOR.width;
		} else {
			// Left-aligned: text ends at dot, so subtract width to position left edge
			if (!isRightAlign) {
				finalTextLeft = position.textLeft - textSize.width;
			}
			// Bottom position: text appears below connector, subtract height for top edge
			if (!isTopPosition) {
				finalTextTop = position.textTop - textSize.height;
			}
		}
	}

	return (
		<Portal>
			{/* Text label - always render for measurement, hide until ready */}
			<Box
				ref={textRef}
				position="fixed"
				top={isReady ? `${finalTextTop}px` : '-9999px'}
				left={isReady ? `${finalTextLeft}px` : '-9999px'}
				zIndex={zIndex + 1}
				pointerEvents="none"
				opacity={isReady ? 1 : 0}
				sx={isReady ? { animation: `${fadeIn} 0.4s ease-out forwards` } : undefined}
			>
				<Text fontSize="md" color="text.softest" whiteSpace="nowrap" px={3} py={1.5} opacity={0.8}>
					{step.text}
				</Text>
			</Box>

			{/* Connector line - only show when ready */}
			{isReady && position && (
				<Box
					position="fixed"
					top={`${position.connectorTop}px`}
					left={`${finalConnectorLeft}px`}
					zIndex={zIndex}
					pointerEvents="none"
					color="text.softest"
					sx={{
						animation: `${fadeIn} 0.4s ease-out forwards`,
						transform: connectorTransform,
						'& svg': {
							opacity: 0.4,
						},
					}}
				>
					<ConnectorComponent />
				</Box>
			)}
		</Portal>
	);
};

OnboardingArrow.displayName = 'OnboardingArrow';
